/**
 * Channel-agnostic, agent-facing reactions. iMessage maps them to Apple tapback
 * codes; RCS sends the emoji as its own message; other threads drop them.
 */
export enum MessageReactionName {
  /** Heart. */
  Love = "love",
  /** Thumbs up. */
  Like = "like",
  /** Thumbs down. */
  Dislike = "dislike",
  /** "Haha". */
  Laugh = "laugh",
  /** Exclamation ("!!"). */
  Emphasize = "emphasize",
  /** Question mark ("?"). */
  Question = "question",
}

export const MESSAGE_REACTION_NAMES = Object.values(MessageReactionName);

/**
 * Parses a stored reaction string back into a `MessageReactionName`, returning
 * `undefined` for null/unknown values so callers can render nothing safely.
 */
export function parseMessageReactionName(
  value: string | null | undefined,
): MessageReactionName | undefined {
  return MESSAGE_REACTION_NAMES.includes(value as MessageReactionName)
    ? (value as MessageReactionName)
    : undefined;
}

/**
 * Native-iMessage-style glyph for each reaction: the tapback badge on the
 * reacted-to bubble, and the whole message where a tapback cannot land (RCS).
 */
export function messageReactionNameToEmoji(
  reaction: MessageReactionName,
): string {
  switch (reaction) {
    case MessageReactionName.Love:
      return "❤️";
    case MessageReactionName.Like:
      return "👍";
    case MessageReactionName.Dislike:
      return "👎";
    case MessageReactionName.Laugh:
      return "😂";
    case MessageReactionName.Emphasize:
      return "‼️";
    case MessageReactionName.Question:
      return "❓";
  }
}

/**
 * Apple tapback "add" code for each semantic reaction (mirrors
 * `IMessageReactionType`), kept as raw numbers so redo-model stays dependency-free.
 */
export function messageReactionNameToAppleCode(
  reaction: MessageReactionName,
): number {
  switch (reaction) {
    case MessageReactionName.Love:
      return 2000;
    case MessageReactionName.Like:
      return 2001;
    case MessageReactionName.Dislike:
      return 2002;
    case MessageReactionName.Laugh:
      return 2003;
    case MessageReactionName.Emphasize:
      return 2004;
    case MessageReactionName.Question:
      return 2005;
  }
}
